import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getDotWindow } from '../useWindowedDots';

// Minimal image shape shared by ListingCard's `listing.images` and the AMP
// flow's `ListingImageData`. Only the fields the carousel needs are required.
export interface PhotoCarouselImage {
  id?: string | number;
  url: string;
  thumbnailUrl?: string;
  // The source the caller explicitly selected for large inline rendering.
  // Keeping it separate from `url` prevents the generic carousel from deciding
  // on its own that an arbitrary original is suitable for a page-wide hero.
  displayUrl?: string;
}

export interface PhotoCarouselLabels {
  prev: string;
  next: string;
  goTo: (index: number) => string;
}

// Which image purpose the slides serve. Cards use thumbnails; page-wide heroes
// use a display source explicitly selected by their caller.
export type PhotoCarouselImageSize = 'thumbnail' | 'display';

// A display-mode caller must explicitly provide its chosen source. Keep the
// fallback local so malformed records never trigger an original-image download
// just because both display and thumbnail URLs are absent.
const DISPLAY_PLACEHOLDER =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1" height="1"%3E%3Cpath fill="%23eff3f5" d="M0 0h1v1H0z"/%3E%3C/svg%3E';

/** Resolve the slide `src` without treating an arbitrary original as display-safe. */
export const pickSlideSrc = (
  img: Pick<PhotoCarouselImage, 'url' | 'thumbnailUrl' | 'displayUrl'>,
  imageSize: PhotoCarouselImageSize,
): string => (
  imageSize === 'display'
    ? img.displayUrl || img.thumbnailUrl || DISPLAY_PLACEHOLDER
    : img.thumbnailUrl || img.url
);

/** Prioritize only the initially visible page-wide hero; defer every other slide. */
export const getSlideLoadingProps = (
  imageSize: PhotoCarouselImageSize,
  index: number,
): Pick<React.ImgHTMLAttributes<HTMLImageElement>, 'loading' | 'fetchPriority'> => {
  const isInitialDisplaySlide = imageSize === 'display' && index === 0;

  return {
    loading: isInitialDisplaySlide ? 'eager' : 'lazy',
    fetchPriority: isInitialDisplaySlide ? 'high' : undefined,
  };
};

interface PhotoCarouselProps {
  // BEM block prefix used to build all class names (e.g. `listing-card`,
  // `amp-share`), so each consumer keeps its own styling while sharing this
  // browsing logic.
  block: string;
  images: PhotoCarouselImage[];
  // Alt text for the currently visible slide (other slides get empty alt).
  activeAlt?: string;
  // Defaults to the thumbnail variant; large standalone galleries opt into the
  // display source selected by their caller.
  imageSize?: PhotoCarouselImageSize;
  // Force-disable swiping even with multiple images.
  disableSwipe?: boolean;
  labels?: PhotoCarouselLabels;
  // Slots rendered inside the visual wrapper.
  badge?: React.ReactNode;
  overlay?: React.ReactNode;
  // Rendered when there are no images (e.g. a house graphic).
  emptyContent?: React.ReactNode;
  // Extra absolutely-positioned controls (e.g. a "view all photos" button).
  trailingControls?: React.ReactNode;
  // Opens the photo that is currently in view (e.g. in a lightbox). Omit to keep
  // the photo area inert, which is what card consumers want — there the click
  // belongs to the card itself.
  onImageClick?: (image: PhotoCarouselImage, index: number) => void;
  className?: string;
}

const DEFAULT_LABELS: PhotoCarouselLabels = {
  prev: 'Previous photo',
  next: 'Next photo',
  goTo: (index: number) => `Go to photo ${index + 1}`,
};

/** Prev/next chevrons for the photo nav (hover-revealed, pointer only). */
const ChevronIcon = ({ dir }: { dir: 'left' | 'right' }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d={dir === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Shared swipeable photo carousel extracted from ListingCard. Owns the visual
 * wrapper and all browsing logic: a translated photo track (drag/swipe on
 * touch), windowed pager dots, a photo count, and hover prev/next chevrons.
 * Layout/sizing comes from the consumer's `${block}__*` styles.
 */
const PhotoCarousel: React.FC<PhotoCarouselProps> = ({
  block,
  images,
  activeAlt = '',
  imageSize = 'thumbnail',
  disableSwipe = false,
  labels = DEFAULT_LABELS,
  badge,
  overlay,
  emptyContent,
  trailingControls,
  onImageClick,
  className,
}) => {
  const [imageIndex, setImageIndex] = useState(0);

  const hasImages = images.length > 0;
  const safeIndex = Math.min(imageIndex, Math.max(images.length - 1, 0));
  const swipeable = !disableSwipe && hasImages && images.length > 1;

  const visualRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const swipe = useRef({ startX: 0, startY: 0, axis: '' as '' | 'x' | 'y', active: false });

  /** Position the photo track: translateX(-index*100% + live drag offset). */
  const setTrackX = useCallback((idx: number, dx: number, animate: boolean) => {
    const tr = trackRef.current;
    if (!tr) return;
    tr.style.transition = animate ? 'transform 0.3s ease' : 'none';
    tr.style.transform = `translateX(calc(${-idx * 100}% + ${dx}px))`;
  }, []);

  useEffect(() => {
    setTrackX(safeIndex, 0, true);
  }, [safeIndex, setTrackX]);

  useEffect(() => {
    const el = visualRef.current;
    if (!el || !swipeable) return undefined;
    const DEADZONE = 8;
    const THRESHOLD = 40;
    const last = images.length - 1;

    const onStart = (e: TouchEvent) => {
      const tch = e.touches[0];
      swipe.current = { startX: tch.clientX, startY: tch.clientY, axis: '', active: false };
    };
    const onMove = (e: TouchEvent) => {
      const s = swipe.current;
      const tch = e.touches[0];
      const dx = tch.clientX - s.startX;
      const dy = tch.clientY - s.startY;
      if (!s.axis) {
        if (Math.abs(dx) < DEADZONE && Math.abs(dy) < DEADZONE) return;
        s.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        s.active = s.axis === 'x';
      }
      if (s.axis !== 'x') return;
      e.preventDefault();
      const atEnd = (safeIndex === 0 && dx > 0) || (safeIndex === last && dx < 0);
      setTrackX(safeIndex, atEnd ? dx * 0.3 : dx, false);
    };
    const onEnd = (e: TouchEvent) => {
      const s = swipe.current;
      if (s.active && s.axis === 'x') {
        const dx = (e.changedTouches[0]?.clientX ?? s.startX) - s.startX;
        let next = safeIndex;
        if (dx <= -THRESHOLD && safeIndex < last) next = safeIndex + 1;
        else if (dx >= THRESHOLD && safeIndex > 0) next = safeIndex - 1;
        if (next !== safeIndex) setImageIndex(next);
        else setTrackX(safeIndex, 0, true);
      }
      swipe.current = { startX: 0, startY: 0, axis: '', active: false };
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [swipeable, safeIndex, images.length, setTrackX]);

  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const goToImage = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setImageIndex(index);
  };

  // Keep the card's open/click gesture from firing mid-swipe on touch.
  const shieldHandlers = swipeable
    ? {
      onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
      onPointerMove: (e: React.PointerEvent) => e.stopPropagation(),
    }
    : {};

  const { dotWindow, isEdgeDot } = getDotWindow(safeIndex, images.length);

  const photoNav = images.length > 1 && (
    <>
      <div className={`${block}__pager`} onClick={stop}>
        <span className={`${block}__pager-dots`}>
          {dotWindow.map((i) => (
            <button
              type="button"
              key={images[i]?.id ?? `dot-${i}`}
              className={`${block}__pager-dot${
                i === safeIndex ? ` ${block}__pager-dot--active` : ''
              }${isEdgeDot(i) ? ` ${block}__pager-dot--edge` : ''}`}
              aria-label={labels.goTo(i)}
              onClick={(e) => goToImage(e, i)}
            />
          ))}
        </span>
      </div>
      <span className={`${block}__pager-count`} aria-hidden="true">
        {safeIndex + 1}/{images.length}
      </span>
      {safeIndex > 0 && (
        <button
          type="button"
          className={`${block}__nav ${block}__nav--prev`}
          aria-label={labels.prev}
          onClick={(e) => {
            stop(e);
            setImageIndex(safeIndex - 1);
          }}
        >
          <ChevronIcon dir="left" />
        </button>
      )}
      {safeIndex < images.length - 1 && (
        <button
          type="button"
          className={`${block}__nav ${block}__nav--next`}
          aria-label={labels.next}
          onClick={(e) => {
            stop(e);
            setImageIndex(safeIndex + 1);
          }}
        >
          <ChevronIcon dir="right" />
        </button>
      )}
    </>
  );

  return (
    <div
      className={`${block}__visual${swipeable ? ` ${block}__visual--swipeable` : ''}${
        className ? ` ${className}` : ''
      }`}
      ref={visualRef}
      {...shieldHandlers}
    >
      {badge}
      {hasImages ? (
        <>
          <div
            className={`${block}__track`}
            ref={trackRef}
            onClick={
              onImageClick
                ? (e) => {
                  e.stopPropagation();
                  onImageClick(images[safeIndex], safeIndex);
                }
                : undefined
            }
          >
            {images.map((img, i) => (
              <img
                key={img.id ?? `${img.url}-${i}`}
                className={`${block}__slide`}
                src={pickSlideSrc(img, imageSize)}
                alt={i === safeIndex ? activeAlt : ''}
                {...getSlideLoadingProps(imageSize, i)}
                decoding="async"
                draggable={false}
              />
            ))}
          </div>
          {overlay}
        </>
      ) : (
        emptyContent
      )}
      {photoNav}
      {trailingControls}
    </div>
  );
};

export default PhotoCarousel;
